import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Send, Plus, ChevronUp, ChevronDown, X, Search, GripVertical, BookOpen } from 'lucide-react'
import { courseApi, lessonApi } from '../api/lessonBuilderApi'
import { countCards } from '../lib/sections'
import { compressImageFile } from '../../../utils/imageCompress'
import FileDropzone from '../components/FileDropzone'
import { COURSE_LEVELS, type CourseDoc, type CourseLevel } from '../types/CourseDoc'
import type { LessonDoc } from '../types/LessonDoc'

const IMG_ACCEPT = { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif'] }

// Trình soạn thảo danh sách gạch đầu dòng (Bạn sẽ học được gì / Yêu cầu).
const ListEditor: React.FC<{
  items: string[]
  placeholder: string
  onChange: (items: string[]) => void
}> = ({ items, placeholder, onChange }) => {
  const set = (i: number, v: string) => onChange(items.map((x, k) => (k === i ? v : x)))
  const add = () => onChange([...items, ''])
  const remove = (i: number) => onChange(items.filter((_, k) => k !== i))
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
          <input
            className="flex-1 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
            value={it}
            placeholder={placeholder}
            onChange={(e) => set(i, e.target.value)}
          />
          <button onClick={() => remove(i)} className="p-1 text-gray-300 hover:text-red-600">
            <X size={15} />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700"
      >
        <Plus size={14} /> Thêm dòng
      </button>
    </div>
  )
}

// Trang soạn khoá học: thông tin khoá + cài đặt chuyên nghiệp + chọn/sắp xếp bài giảng.
const CourseBuilderPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [course, setCourse] = useState<CourseDoc | null>(null)
  const [allLessons, setAllLessons] = useState<LessonDoc[]>([])
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [coverBusy, setCoverBusy] = useState(false)
  const [search, setSearch] = useState('')
  const [dragId, setDragId] = useState<string | null>(null)

  useEffect(() => {
    if (id) courseApi.get(id).then(setCourse)
    lessonApi.list().then(setAllLessons)
  }, [id])

  const lessonsInCourse = useMemo(
    () =>
      course
        ? (course.lessonIds.map((lid) => allLessons.find((l) => l.id === lid)).filter(Boolean) as LessonDoc[])
        : [],
    [course, allLessons]
  )
  const availableLessons = useMemo(() => {
    if (!course) return []
    const q = search.trim().toLowerCase()
    return allLessons
      .filter((l) => !course.lessonIds.includes(l.id))
      .filter((l) => !q || (l.meta.tieuDe || '').toLowerCase().includes(q))
  }, [course, allLessons, search])

  if (!course) {
    return <div className="p-10 text-center text-gray-400">Không tìm thấy khoá học.</div>
  }

  const update = (patch: Partial<CourseDoc>) => setCourse({ ...course, ...patch })

  const totalCards = lessonsInCourse.reduce((s, l) => s + countCards(l), 0)

  const addLesson = (lid: string) => update({ lessonIds: [...course.lessonIds, lid] })
  const removeLesson = (lid: string) => update({ lessonIds: course.lessonIds.filter((x) => x !== lid) })
  const moveLesson = (lid: string, dir: -1 | 1) => {
    const ids = [...course.lessonIds]
    const i = ids.indexOf(lid)
    const j = i + dir
    if (i < 0 || j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    update({ lessonIds: ids })
  }
  // Kéo-thả sắp xếp bài giảng trong khoá.
  const onDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) return
    const ids = [...course.lessonIds]
    const from = ids.indexOf(dragId)
    const to = ids.indexOf(targetId)
    if (from < 0 || to < 0) return
    ids.splice(to, 0, ids.splice(from, 1)[0])
    update({ lessonIds: ids })
    setDragId(null)
  }

  const handleSave = async (publish: boolean) => {
    if (!course.title.trim()) {
      alert('Vui lòng nhập tên khoá học.')
      return
    }
    if (publish && course.lessonIds.length === 0) {
      alert('Hãy thêm ít nhất 1 bài giảng trước khi xuất bản.')
      return
    }
    try {
      const saved = await courseApi.save(course)
      setCourse(saved)
      setSavedAt(new Date().toLocaleTimeString('vi-VN'))
      if (publish) {
        const pub = await courseApi.publish(saved.id)
        navigate(`/khoa-hoc/${(pub || saved).slug}`)
      }
    } catch (e) {
      alert('Lưu khoá học THẤT BẠI (server chưa nhận):\n' + ((e as Error)?.message || e) +
        '\n\nKhoá CHƯA được lưu. Hãy chụp lỗi này gửi hỗ trợ.')
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/teacher/khoa-hoc')} className="text-gray-400 hover:text-gray-700">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-black text-gray-900">Soạn khoá học</h1>
          {savedAt && <span className="text-xs text-gray-400">Đã lưu lúc {savedAt}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave(false)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200"
          >
            <Save size={16} /> Lưu nháp
          </button>
          <button
            onClick={() => handleSave(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700"
          >
            <Send size={16} /> Xuất bản
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 max-w-6xl mx-auto">
        {/* Thông tin & cài đặt khoá */}
        <section className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Thông tin khoá học</h3>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Tên khoá học</label>
              <input
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.title}
                onChange={(e) => update({ title: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Mô tả ngắn</label>
              <input
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.subtitle || ''}
                onChange={(e) => update({ subtitle: e.target.value })}
                placeholder="1 câu tóm tắt khoá học"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Chủ đề / Lớp</label>
                <input
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                  value={course.category || ''}
                  onChange={(e) => update({ category: e.target.value })}
                  placeholder="Vd: Vật lí 11"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Trình độ</label>
                <select
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
                  value={course.level || ''}
                  onChange={(e) => update({ level: (e.target.value || '') as CourseLevel | '' })}
                >
                  <option value="">— Chọn —</option>
                  {COURSE_LEVELS.map((lv) => (
                    <option key={lv.value} value={lv.value}>
                      {lv.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Quyền truy cập</label>
              <select
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
                value={course.access || 'free'}
                onChange={(e) => update({ access: (e.target.value === 'pro' ? 'pro' : 'free') })}
              >
                <option value="free">Miễn phí (ai cũng ghi danh được)</option>
                <option value="pro">Yêu cầu tài khoản PRO</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Giới thiệu chi tiết</label>
              <textarea
                className="w-full min-h-[90px] p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.description || ''}
                onChange={(e) => update({ description: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Ảnh bìa khoá học</label>
              {course.coverImage ? (
                <div className="relative mt-1 group">
                  <img src={course.coverImage} alt="Ảnh bìa" className="w-full h-36 object-cover rounded-xl border border-gray-200" />
                  <button
                    onClick={() => update({ coverImage: undefined })}
                    className="absolute top-2 right-2 px-2 py-1 rounded-lg bg-white/90 text-red-600 text-xs font-bold shadow hover:bg-white"
                  >
                    Xoá ảnh
                  </button>
                </div>
              ) : (
                <FileDropzone
                  accept={IMG_ACCEPT}
                  busy={coverBusy}
                  label="Kéo-thả ảnh bìa"
                  hint="PNG, JPG, WebP — nên dùng ảnh ngang (16:9)"
                  onFile={async (file) => {
                    setCoverBusy(true)
                    try {
                      update({ coverImage: await compressImageFile(file, { maxWidth: 1600, maxHeight: 900 }) })
                    } finally {
                      setCoverBusy(false)
                    }
                  }}
                />
              )}
              <input
                className="w-full mt-2 p-2 border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-200"
                value={course.coverImage && !course.coverImage.startsWith('data:') ? course.coverImage : ''}
                onChange={(e) => update({ coverImage: e.target.value || undefined })}
                placeholder="hoặc dán URL ảnh bìa"
              />
            </div>
          </div>

          {/* Giảng viên */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Giảng viên</h3>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Tên giảng viên</label>
              <input
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.instructorName || ''}
                onChange={(e) => update({ instructorName: e.target.value })}
                placeholder="Vd: Thầy Nguyễn Văn A"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Trường / Đơn vị</label>
              <input
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.instructorSchool || ''}
                onChange={(e) => update({ instructorSchool: e.target.value })}
                placeholder="Vd: THPT Chuyên ..."
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Kinh nghiệm / Giới thiệu</label>
              <textarea
                className="w-full min-h-[70px] p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.instructorExperience || ''}
                onChange={(e) => update({ instructorExperience: e.target.value })}
                placeholder="Vd: 10 năm luyện thi HSG, đạt..."
              />
            </div>
          </div>

          {/* Bạn sẽ học được gì */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Bạn sẽ học được gì</h3>
            <ListEditor
              items={course.outcomes || []}
              placeholder="Vd: Hiểu định luật bảo toàn năng lượng"
              onChange={(outcomes) => update({ outcomes })}
            />
          </div>

          {/* Yêu cầu đầu vào */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Yêu cầu đầu vào</h3>
            <ListEditor
              items={course.requirements || []}
              placeholder="Vd: Nắm vững kiến thức Vật lí 10"
              onChange={(requirements) => update({ requirements })}
            />
          </div>
        </section>

        {/* Bài giảng trong khoá + thêm bài */}
        <section className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">
                Nội dung khoá học ({lessonsInCourse.length} bài)
              </h3>
              <span className="text-[11px] text-gray-400">{totalCards} mục nội dung</span>
            </div>
            {lessonsInCourse.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">Chưa có bài. Thêm từ danh sách bên dưới.</p>
            ) : (
              <div className="space-y-2">
                {lessonsInCourse.map((l, i) => (
                  <div
                    key={l.id}
                    draggable
                    onDragStart={() => setDragId(l.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(l.id)}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border transition-colors ${
                      dragId === l.id ? 'border-blue-300 bg-blue-50/60' : 'border-gray-100'
                    }`}
                  >
                    <GripVertical size={16} className="text-gray-300 cursor-grab shrink-0" />
                    <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-gray-800 truncate">{l.meta.tieuDe}</span>
                      <span className="text-[11px] text-gray-400">{countCards(l)} mục</span>
                    </div>
                    <button onClick={() => moveLesson(l.id, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                      <ChevronUp size={16} />
                    </button>
                    <button onClick={() => moveLesson(l.id, 1)} disabled={i === lessonsInCourse.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                      <ChevronDown size={16} />
                    </button>
                    <button onClick={() => removeLesson(l.id)} className="p-1 text-gray-400 hover:text-red-600">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Thêm bài giảng</h3>
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
              <input
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm bài giảng theo tên..."
              />
            </div>
            {availableLessons.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">
                {search.trim()
                  ? 'Không tìm thấy bài giảng phù hợp.'
                  : 'Không còn bài nào. Tạo bài giảng ở mục "Bài giảng của tôi".'}
              </p>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {availableLessons.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => addLesson(l.id)}
                    className="w-full flex items-center gap-2 p-2.5 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/40 text-left"
                  >
                    <BookOpen size={16} className="text-gray-300 shrink-0" />
                    <span className="flex-1 text-sm font-semibold text-gray-800 truncate">{l.meta.tieuDe}</span>
                    <span className="text-[11px] text-gray-400">{countCards(l)} mục</span>
                    <Plus size={16} className="text-blue-600 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default CourseBuilderPage
